import { useEffect, useState } from 'react'
import api from '@/services/api'

/**
 * iter-13a: brand colour picker for the hosted storefront.
 *
 * Reads and writes `settings.branding_settings.primary_color` and
 * `secondary_color`. The public `GET /v1/vendors/{username}` handler picks
 * these up and returns them under a top-level `branding` block; the
 * storefront wires that block into CSS custom properties on VendorShell
 * so every branded surface (header, footer, PDP CTAs, checkout buttons)
 * picks up the supplier's identity without any code change.
 *
 * The API validation only accepts the strict `#rrggbb` form, so this UI
 * mirrors the same regex client-side and normalises abbreviated (`#rgb`)
 * input on blur.
 */
interface BrandColourSectionProps {
  vendorId?: string
}

const HEX_LONG = /^#[0-9a-fA-F]{6}$/
const HEX_SHORT = /^#[0-9a-fA-F]{3}$/
const DEFAULT_PRIMARY = '#455461'

function expandShortHex(value: string): string {
  return (
    '#' +
    value
      .slice(1)
      .split('')
      .map((c) => c + c)
      .join('')
      .toLowerCase()
  )
}

export default function BrandColourSection({ vendorId }: BrandColourSectionProps) {
  const [primary, setPrimary] = useState<string>(DEFAULT_PRIMARY)
  const [secondary, setSecondary] = useState<string>('')
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [success, setSuccess] = useState<string | null>(null)

  useEffect(() => {
    if (!vendorId) return
    api.vendors
      .get(vendorId)
      .then((res) => {
        const data = res.data?.data ?? res.data
        const branding = data?.settings?.branding_settings
        if (branding?.primary_color) setPrimary(branding.primary_color)
        if (branding?.secondary_color) setSecondary(branding.secondary_color)
      })
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [vendorId])

  const normalise = (value: string): string | null => {
    const trimmed = value.trim().toLowerCase()
    if (!trimmed) return null
    if (HEX_LONG.test(trimmed)) return trimmed
    if (HEX_SHORT.test(trimmed)) return expandShortHex(trimmed)
    return null
  }

  const save = async () => {
    if (!vendorId) return
    setError(null)
    setSuccess(null)

    const normalisedPrimary = normalise(primary)
    if (!normalisedPrimary) {
      setError('Primary colour must be a hex value like #455461.')
      return
    }
    const normalisedSecondary = secondary.trim() ? normalise(secondary) : null
    if (secondary.trim() && !normalisedSecondary) {
      setError('Secondary colour must be a hex value like #8899aa or left blank.')
      return
    }

    setSaving(true)
    try {
      const brandingSettings: Record<string, string> = {
        primaryColor: normalisedPrimary,
      }
      if (normalisedSecondary) {
        brandingSettings.secondaryColor = normalisedSecondary
      }
      await api.vendors.update(vendorId, {
        settings: { brandingSettings },
      })
      setPrimary(normalisedPrimary)
      if (normalisedSecondary) setSecondary(normalisedSecondary)
      setSuccess('Brand colour saved. Your storefront will reflect the change within a few minutes.')
    } catch (e: any) {
      setError(e?.response?.data?.error?.message || 'Could not save brand colour.')
    } finally {
      setSaving(false)
    }
  }

  if (!vendorId) return null

  return (
    <div className="card">
      <h3 style={{ marginBottom: '0.5rem' }}>Brand colour</h3>
      <p style={{ color: 'var(--color-gray-600)', marginBottom: '1.5rem', fontSize: '0.9rem' }}>
        Applied across your hosted storefront: header accent, footer links, primary CTAs on
        product pages and the checkout flow.
      </p>

      {loading ? (
        <p style={{ color: 'var(--color-gray-500)' }}>Loading current colours...</p>
      ) : (
        <>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>
            <div>
              <label style={labelStyle}>Primary colour</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={HEX_LONG.test(primary) ? primary : DEFAULT_PRIMARY}
                  onChange={(e) => setPrimary(e.target.value)}
                  style={{
                    width: '3rem',
                    height: '2.4rem',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px',
                    padding: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  aria-label="Primary colour picker"
                />
                <input
                  type="text"
                  value={primary}
                  onChange={(e) => setPrimary(e.target.value)}
                  placeholder="#455461"
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              <p style={helperStyle}>Used for CTAs, links, and header accent.</p>
            </div>

            <div>
              <label style={labelStyle}>Secondary colour (optional)</label>
              <div style={{ display: 'flex', gap: '0.75rem', alignItems: 'center' }}>
                <input
                  type="color"
                  value={secondary && HEX_LONG.test(secondary) ? secondary : '#cccccc'}
                  onChange={(e) => setSecondary(e.target.value)}
                  style={{
                    width: '3rem',
                    height: '2.4rem',
                    border: '1px solid var(--color-gray-300)',
                    borderRadius: '6px',
                    padding: 0,
                    background: 'transparent',
                    cursor: 'pointer',
                  }}
                  aria-label="Secondary colour picker"
                />
                <input
                  type="text"
                  value={secondary}
                  onChange={(e) => setSecondary(e.target.value)}
                  placeholder="Leave blank to skip"
                  style={{ ...inputStyle, flex: 1 }}
                />
              </div>
              <p style={helperStyle}>Reserved for supporting accents. Optional.</p>
            </div>
          </div>

          {error && (
            <p style={{ color: 'var(--color-error)', marginTop: '1rem', fontSize: '0.9rem' }}>
              {error}
            </p>
          )}
          {success && (
            <p style={{ color: 'var(--color-success)', marginTop: '1rem', fontSize: '0.9rem' }}>
              {success}
            </p>
          )}

          <button
            type="button"
            onClick={save}
            disabled={saving}
            style={{
              marginTop: '1.25rem',
              padding: '0.6rem 1.25rem',
              borderRadius: '6px',
              border: 'none',
              background: saving ? 'var(--color-gray-400)' : 'var(--color-tixxly-navy)',
              color: 'white',
              cursor: saving ? 'not-allowed' : 'pointer',
              fontSize: '0.9rem',
              fontWeight: 500,
            }}
          >
            {saving ? 'Saving...' : 'Save brand colour'}
          </button>
        </>
      )}
    </div>
  )
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '0.35rem',
  fontSize: '0.85rem',
  fontWeight: 500,
  color: 'var(--color-gray-700)',
}

const inputStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  border: '1px solid var(--color-gray-300)',
  borderRadius: '6px',
  fontSize: '0.9rem',
}

const helperStyle: React.CSSProperties = {
  marginTop: '0.4rem',
  fontSize: '0.8rem',
  color: 'var(--color-gray-500)',
}
